import { Platform } from 'react-native';
import SimpleStorage from '../utils/simpleStorage';
import { Product } from '../interfaces/Product';

class ProductManagementService {
  /**
   * Load products for a business owner
   * @param businessOwnerId - The ID of the business owner
   * @returns Array of products
   */
  async loadProducts(businessOwnerId: string): Promise<Product[]> {
    try {
      let loadedProducts: Product[] = [];
      let loadedFromFirestore = false;

      // Try to load from Firestore first (most up-to-date)
      if (Platform.OS === 'web') {
        try {
          // Use require() to ensure same module instance as unifiedAuthService
          const { doc, getDoc } = require('firebase/firestore');
          const { getWebDb, initializeFirebase } = require('./unifiedAuthService');

          // Ensure Firebase is initialized
          const initialized = initializeFirebase();
          if (!initialized) {
            console.warn('⚠️  ProductManagementService: Firebase not initialized, skipping Firestore load');
          } else {
            const firestoreDb = getWebDb();
            if (!firestoreDb) {
              console.warn('⚠️  ProductManagementService: No Firebase database instance found, skipping Firestore load');
            } else {
              const productsDocRef = doc(firestoreDb, 'businesses', businessOwnerId, 'products', 'all');
              console.log(`🔍 ProductManagementService: Attempting to load products from Firestore path: businesses/${businessOwnerId}/products/all`);
              const productsDoc = await getDoc(productsDocRef);

              if (productsDoc.exists()) {
                const data = productsDoc.data();
                loadedProducts = data.products || [];
                loadedFromFirestore = true;
                console.log(`✅ ProductManagementService: Loaded ${loadedProducts.length} products from Firestore`);

                // Sync to local storage for offline access
                await SimpleStorage.setItem(`products_${businessOwnerId}`, JSON.stringify(loadedProducts));
              } else {
                console.warn(`⚠️  ProductManagementService: Products document does not exist in Firestore for business ${businessOwnerId}`);
              }
            }
          }
        } catch (firestoreError: any) {
          console.warn('⚠️  ProductManagementService: Could not load products from Firestore, trying local storage:', firestoreError);
        }
      } else {
        // Mobile: load from Firestore first (same DB as web)
        try {
          const { getMobileFirestore, initializeFirebase } = require('./unifiedAuthService');
          initializeFirebase();
          const getFirestore = getMobileFirestore?.();
          if (getFirestore && typeof getFirestore === 'function') {
            const db = getFirestore();
            if (db && typeof db.collection === 'function' && businessOwnerId) {
              const productsDoc = await db
                .collection('businesses')
                .doc(businessOwnerId)
                .collection('products')
                .doc('all')
                .get();
              if (productsDoc.exists) {
                const data = productsDoc.data() || {};
                loadedProducts = data.products || [];
                loadedFromFirestore = true;
                console.log(`✅ ProductManagementService: Loaded ${loadedProducts.length} products from Firestore (mobile)`);
                await SimpleStorage.setItem(`products_${businessOwnerId}`, JSON.stringify(loadedProducts));
              }
            }
          }
        } catch (firestoreError: any) {
          console.warn('⚠️  ProductManagementService: Could not load products from Firestore on mobile:', firestoreError?.message);
        }
      }

      // Fallback to local storage if Firestore didn't work
      if (!loadedFromFirestore) {
        await SimpleStorage.forceInitialize();
        const savedProducts = await SimpleStorage.getItem(`products_${businessOwnerId}`);
        if (savedProducts) {
          loadedProducts = JSON.parse(savedProducts);
          console.log(`✅ ProductManagementService: Loaded ${loadedProducts.length} products from local storage`);
        }
      }

      return loadedProducts;
    } catch (error) {
      console.error('❌ ProductManagementService: Error loading products:', error);
      return [];
    }
  }

  /**
   * Save products for a business owner
   * @param businessOwnerId - The ID of the business owner
   * @param products - Array of products to save
   */
  async saveProducts(businessOwnerId: string, products: Product[]): Promise<void> {
    try {
      // Save to local storage (for offline access)
      await SimpleStorage.forceInitialize();
      await SimpleStorage.setItem(`products_${businessOwnerId}`, JSON.stringify(products));
      console.log(`✅ ProductManagementService: Products saved to local storage`);

      // Remove undefined values from products recursively (Firestore doesn't allow undefined)
      const removeUndefined = (obj: any): any => {
        if (obj === null || obj === undefined) {
          return null;
        }
        if (Array.isArray(obj)) {
          return obj.map(removeUndefined).filter(item => item !== undefined);
        }
        if (typeof obj === 'object') {
          const cleaned: any = {};
          Object.keys(obj).forEach(key => {
            const value = obj[key];
            if (value !== undefined) {
              cleaned[key] = removeUndefined(value);
            }
          });
          return cleaned;
        }
        return obj;
      };
      const cleanProducts = products.map(product => removeUndefined(product));
      const payload = { products: cleanProducts, updatedAt: new Date().toISOString() };

      // Also save to Firestore (for cross-browser/device access)
      if (Platform.OS === 'web') {
        try {
          const { doc, setDoc } = require('firebase/firestore');
          const { getWebDb, initializeFirebase } = require('./unifiedAuthService');
          const initialized = initializeFirebase();
          if (!initialized) {
            console.warn('⚠️  ProductManagementService: Firebase not initialized, skipping Firestore save');
          } else {
            const firestoreDb = getWebDb();
            if (!firestoreDb) {
              console.warn('⚠️  ProductManagementService: No Firebase database instance found, skipping Firestore save');
            } else {
              const productsDocRef = doc(firestoreDb, 'businesses', businessOwnerId, 'products', 'all');
              await setDoc(productsDocRef, payload);
              console.log(`✅ ProductManagementService: Products saved to Firestore`);
            }
          }
        } catch (firestoreError: any) {
          console.error('❌ ProductManagementService: Error saving products to Firestore:', firestoreError);
          console.warn('⚠️  ProductManagementService: Products saved locally but not in Firestore');
        }
      } else {
        // Mobile: save to Firestore (same path as web)
        try {
          const { getMobileFirestore, initializeFirebase } = require('./unifiedAuthService');
          initializeFirebase();
          const getFirestore = getMobileFirestore?.();
          if (getFirestore && typeof getFirestore === 'function') {
            const db = getFirestore();
            if (db && typeof db.collection === 'function' && businessOwnerId) {
              await db
                .collection('businesses')
                .doc(businessOwnerId)
                .collection('products')
                .doc('all')
                .set(payload);
              console.log(`✅ ProductManagementService: Products saved to Firestore (mobile)`);
            }
          }
        } catch (firestoreError: any) {
          console.warn('⚠️  ProductManagementService: Could not save products to Firestore on mobile:', firestoreError?.message);
        }
      }
    } catch (error) {
      console.error('❌ ProductManagementService: Error saving products:', error);
      throw error;
    }
  }
}

// Export a singleton instance
const productManagementService = new ProductManagementService();
export default productManagementService;
export { productManagementService };
